/**
 * Workspace file-format resolution — maps the server-derived `fileType`
 * (with contentType/extension fallbacks for rows predating file-type
 * validation) to the viewer that should render the file.
 *
 * `fileType` casing is not guaranteed: file DTOs and document-detail send
 * PascalCase member names ("Pdf", "Xlsx"), but older backend builds emitted
 * camelCase ("pdf", "xlsx"). Resolution is case-insensitive.
 */

export type WorkspaceFileFormat = "pdf" | "xls" | "xlsx" | "doc" | "docx" | "unknown";

/**
 * Which embedded viewer handles a format. `.doc` renders in the DOCX viewer via
 * server-converted preview bytes (GET /files/{id}/preview-content); unknown
 * formats are download-only.
 */
export type ViewerKind = "pdf" | "excel" | "docx" | "doc" | "unsupported";

const FORMAT_NAMES: ReadonlySet<string> = new Set(["pdf", "xls", "xlsx", "doc", "docx"]);

const MIME_TO_FORMAT: Record<string, WorkspaceFileFormat> = {
    "application/pdf": "pdf",
    "application/x-pdf": "pdf",
    "application/vnd.ms-excel": "xls",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": "xlsx",
    "application/msword": "doc",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": "docx",
};

/**
 * Resolve the canonical format for a stored file.
 * Priority: server `fileType` → stored `contentType` → filename extension.
 */
export function resolveFileFormat(fileType?: string | null, contentType?: string | null, fileName?: string | null): WorkspaceFileFormat {
    const normalizedType = fileType?.trim().toLowerCase();
    if (normalizedType && FORMAT_NAMES.has(normalizedType)) {
        return normalizedType as WorkspaceFileFormat;
    }

    const mime = contentType?.split(";")[0].trim().toLowerCase();
    if (mime && MIME_TO_FORMAT[mime]) {
        return MIME_TO_FORMAT[mime];
    }

    const name = fileName?.toLowerCase() ?? "";
    const lastDot = name.lastIndexOf(".");
    const extension = lastDot >= 0 ? name.slice(lastDot + 1) : "";
    if (FORMAT_NAMES.has(extension)) {
        return extension as WorkspaceFileFormat;
    }

    return "unknown";
}

/** Route a format to its viewer. */
export function viewerKindFor(format: WorkspaceFileFormat): ViewerKind {
    switch (format) {
        case "pdf":
            return "pdf";
        case "xls":
        case "xlsx":
            return "excel";
        case "docx":
            return "docx";
        case "doc":
            // Legacy .doc has no browser renderer (docx-preview is OOXML-only) —
            // it renders via the server-converted .docx preview payload instead
            return "doc";
        default:
            // Unknown formats are download-only by design
            return "unsupported";
    }
}

/** Uppercase label for the workspace file-type badge, e.g. "PDF", "XLSX". */
export function fileFormatLabel(format: WorkspaceFileFormat, fileName?: string | null): string {
    if (format !== "unknown") return format.toUpperCase();
    const name = fileName ?? "";
    const lastDot = name.lastIndexOf(".");
    const extension = lastDot >= 0 ? name.slice(lastDot + 1).toUpperCase() : "";
    return extension.length > 0 && extension.length <= 5 ? extension : "FILE";
}
